import { css } from '@emotion/react'
import {
  gemVar,
  Spacing,
  ThemedColors,
  Transition,
  Typeface,
  ZIndex,
} from '@opallabs/gem'
import * as drawerStyles from '../Drawer/styles'
import { DrawerSize, DrawerSizeType, drawerSizes } from '../Drawer/types'

const rootClassName = 'lapidary-sidebar'
export const selectors = {
  body: `${rootClassName}-body`,
  close: `${rootClassName}-close`,
  content: `${rootClassName}-content`,
  description: `${rootClassName}-description`,
  footer: `${rootClassName}-footer`,
  header: `${rootClassName}-header`,
  root: rootClassName,
  title: `${rootClassName}-title`,
} as const

// Extra space to account for drawer shadow
const POSITION_OFFSET = 40

// How far the sidebar sits past the inline-end edge when it is closed. The
// wrapper below sets it from the sidebar's own width, so a sidebar whose width
// changes at runtime still slides all the way out.
const OFFSCREEN_VARIABLE = '--lapidary-sidebar-offscreen'
const offscreen = `calc(-1 * var(${OFFSCREEN_VARIABLE}))`

const toLength = (size: DrawerSizeType): string =>
  typeof size === 'number' ? `${size}px` : size

export const sidebarContentWrapper = (
  drawerSize: DrawerSize,
  topOffset?: number,
  inlineSize?: DrawerSizeType
) => {
  const width = toLength(inlineSize ?? drawerSizes[drawerSize])

  return css({
    [OFFSCREEN_VARIABLE]: `calc(${width} + ${POSITION_OFFSET}px)`,
    backgroundColor: gemVar(ThemedColors.Surface1),
    borderInlineStart: `solid 1px ${gemVar(ThemedColors.StrokeSecondary1)}`,
    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.10)',
    display: 'flex',
    flexFlow: 'column',
    fontFamily: Typeface.Default,
    height: '100%',
    overflow: 'hidden',
    position: 'fixed',
    // Closed by default. A transition does not run on the first style, so a
    // sidebar that mounts closed does not flash on screen.
    insetInlineEnd: offscreen,
    visibility: 'hidden',
    top: topOffset ?? 0,
    width,
    zIndex: ZIndex.Sidebar.Content,
    // `visibility` does not interpolate: an interpolated `visibility` stays
    // `hidden` until the next frame, so a `focus()` on open would do nothing.
    // It flips at once on open and only after the slide on close. It is first
    // in the list so that, under reduced motion, where only `inset-inline-end`
    // loses its transition, it still gets the first duration and delay.
    ...Transition(['visibility', 'insetInlineEnd'], {
      duration: 500,
      curve: 'ease',
      motionProperties: ['insetInlineEnd'],
    }),
    transitionDuration: '0s, 500ms',
    transitionDelay: '500ms, 0s',
    '&[data-state="open"]': {
      insetInlineEnd: 0,
      visibility: 'visible',
      transitionDelay: '0s, 0s',
      // Gives a sidebar that mounts already open its slide-in.
      '@starting-style': {
        insetInlineEnd: offscreen,
        visibility: 'hidden',
      },
    },
  })
}

export const sidebarHeader = drawerStyles.drawerHeader

export const sidebarBody = drawerStyles.drawerBody

export const sidebarFooter = drawerStyles.drawerFooter

export const title = css({
  ...drawerStyles.title,
  lineHeight: '24px',
  marginBottom: Spacing.L / 2,
  width: 280,
})

export const description = drawerStyles.description

export const close = drawerStyles.close

export const sidebarClose = drawerStyles.drawerClose
